import DxfParser from "dxf-parser";
import type { IDxf, IEntity, ILayer, IPoint } from "dxf-parser";
import { tessellateSpline } from "./bspline";
import type { Point3 } from "./bspline";

/**
 * A segment in Three.js coordinates (x=Easting, y=Height, z=-Northing) with its
 * color. `h1/h2` carry the DXF elevation per endpoint so the line overlay can be
 * drawn as real 3D geometry (not just projected onto the XZ plane).
 */
export interface EdgeSegment {
  x1: number; h1: number; z1: number;
  x2: number; h2: number; z2: number;
  color: number;
}

/**
 * Map DXF coordinates to Three.js coordinates.
 * DXF: X=Easting, Y=Northing, Z=Height
 * Three.js: X=Easting, Y=Height, Z=-Northing
 */
function mapCoord(x: number, y: number, z: number): [number, number, number] {
  return [x, z, -y];
}

const DEFAULT_COLOR = 0xcccccc;

/**
 * Resolve entity color following AutoCAD convention:
 *  - colorIndex 1-255: entity has its own color
 *  - colorIndex 256 (BYLAYER): inherit from layer
 *  - colorIndex 0 (BYBLOCK): inherit from block (fallback to layer then default)
 */
function resolveEntityColor(
  entity: IEntity,
  layers: Record<string, ILayer> | undefined
): number {
  const ci = (entity as any).colorIndex as number | undefined;

  // Entity has its own color (index 1-255)
  if (ci !== undefined && ci >= 1 && ci <= 255) {
    const c = (entity as any).color as number | undefined;
    if (c !== undefined && c !== 0) return c;
  }

  // BYLAYER (256) or BYBLOCK (0) or undefined → resolve from layer
  if (layers && entity.layer) {
    const layer = layers[entity.layer];
    if (layer && layer.color !== undefined && layer.color !== 0) {
      return layer.color;
    }
  }

  return DEFAULT_COLOR;
}

/** Extract edges from a LINE entity */
function extractLineEdges(entity: IEntity, layers: Record<string, ILayer> | undefined): EdgeSegment[] {
  const vertices = (entity as any).vertices as IPoint[] | undefined;
  if (!vertices || vertices.length < 2) return [];

  const color = resolveEntityColor(entity, layers);
  const edges: EdgeSegment[] = [];

  for (let i = 0; i < vertices.length - 1; i++) {
    const [x1, h1, z1] = mapCoord(vertices[i].x, vertices[i].y, vertices[i].z ?? 0);
    const [x2, h2, z2] = mapCoord(vertices[i + 1].x, vertices[i + 1].y, vertices[i + 1].z ?? 0);
    edges.push({ x1, h1, z1, x2, h2, z2, color });
  }
  return edges;
}

/** Extract edges from an LWPOLYLINE entity */
function extractLwPolylineEdges(entity: IEntity, layers: Record<string, ILayer> | undefined): EdgeSegment[] {
  const e = entity as any;
  const vertices = e.vertices as Array<{ x: number; y: number }> | undefined;
  if (!vertices || vertices.length < 2) return [];

  const elevation: number = e.elevation ?? 0;
  const closed: boolean = e.shape ?? false;
  const color = resolveEntityColor(entity, layers);
  const edges: EdgeSegment[] = [];

  for (let i = 0; i < vertices.length - 1; i++) {
    const [x1, h1, z1] = mapCoord(vertices[i].x, vertices[i].y, elevation);
    const [x2, h2, z2] = mapCoord(vertices[i + 1].x, vertices[i + 1].y, elevation);
    edges.push({ x1, h1, z1, x2, h2, z2, color });
  }
  if (closed && vertices.length > 2) {
    const last = vertices[vertices.length - 1];
    const first = vertices[0];
    const [x1, h1, z1] = mapCoord(last.x, last.y, elevation);
    const [x2, h2, z2] = mapCoord(first.x, first.y, elevation);
    edges.push({ x1, h1, z1, x2, h2, z2, color });
  }
  return edges;
}

/** Extract edges from a SPLINE entity */
function extractSplineEdges(entity: IEntity, layers: Record<string, ILayer> | undefined): EdgeSegment[] {
  const e = entity as any;
  const controlPoints = e.controlPoints as IPoint[] | undefined;
  const knotValues = e.knotValues as number[] | undefined;
  const degree: number = e.degreeOfSplineCurve ?? 3;

  if (!controlPoints || controlPoints.length < 2) return [];
  if (!knotValues || knotValues.length < 2) return [];

  const cp: Point3[] = controlPoints.map((p: IPoint) => ({
    x: p.x,
    y: p.y,
    z: p.z ?? 0,
  }));

  const tessellated = tessellateSpline(degree, cp, knotValues, 200);
  if (tessellated.length < 2) return [];

  const color = resolveEntityColor(entity, layers);
  const edges: EdgeSegment[] = [];

  for (let i = 0; i < tessellated.length - 1; i++) {
    const [x1, h1, z1] = mapCoord(tessellated[i].x, tessellated[i].y, tessellated[i].z);
    const [x2, h2, z2] = mapCoord(tessellated[i + 1].x, tessellated[i + 1].y, tessellated[i + 1].z);
    edges.push({ x1, h1, z1, x2, h2, z2, color });
  }
  return edges;
}

/** Tessellate a circular arc (angles in RADIANS, CCW) into edge segments. */
function arcEdges(
  cx: number, cy: number, r: number, a0: number, a1: number,
  elevation: number, color: number
): EdgeSegment[] {
  if (!(r > 0)) return [];
  let span = a1 - a0;
  while (span <= 1e-9) span += Math.PI * 2; // CCW, full circle when a0==a1
  const segs = Math.min(512, Math.max(8, Math.ceil((r * span) / 1.0))); // ~1m chord
  const edges: EdgeSegment[] = [];
  let prev: [number, number, number] | null = null;
  for (let i = 0; i <= segs; i++) {
    const t = a0 + span * (i / segs);
    const [x, h, z] = mapCoord(cx + r * Math.cos(t), cy + r * Math.sin(t), elevation);
    if (prev) edges.push({ x1: prev[0], h1: prev[1], z1: prev[2], x2: x, h2: h, z2: z, color });
    prev = [x, h, z];
  }
  return edges;
}

/** Extract edges from an ARC entity */
function extractArcEdges(entity: IEntity, layers: Record<string, ILayer> | undefined): EdgeSegment[] {
  const e = entity as any;
  if (!e.center || !(e.radius > 0)) return [];
  return arcEdges(e.center.x, e.center.y, e.radius, e.startAngle ?? 0, e.endAngle ?? Math.PI * 2, e.center.z ?? 0, resolveEntityColor(entity, layers));
}

/** Extract edges from a CIRCLE entity */
function extractCircleEdges(entity: IEntity, layers: Record<string, ILayer> | undefined): EdgeSegment[] {
  const e = entity as any;
  if (!e.center || !(e.radius > 0)) return [];
  return arcEdges(e.center.x, e.center.y, e.radius, 0, Math.PI * 2, e.center.z ?? 0, resolveEntityColor(entity, layers));
}

/** Extract edges from an ELLIPSE entity (full or partial) */
function extractEllipseEdges(entity: IEntity, layers: Record<string, ILayer> | undefined): EdgeSegment[] {
  const e = entity as any;
  const c = e.center, ma = e.majorAxisEndPoint;
  if (!c || !ma) return [];
  const ratio = e.axisRatio ?? 1;
  const majLen = Math.hypot(ma.x, ma.y);
  if (!(majLen > 0)) return [];
  const mnx = -ma.y * ratio, mny = ma.x * ratio; // minor axis ⟂ major
  let a0 = e.startAngle ?? 0, a1 = e.endAngle ?? Math.PI * 2;
  let span = a1 - a0;
  while (span <= 1e-9) span += Math.PI * 2;
  const segs = Math.min(512, Math.max(12, Math.ceil((majLen * span) / 1.0)));
  const elevation = c.z ?? 0;
  const color = resolveEntityColor(entity, layers);
  const edges: EdgeSegment[] = [];
  let prev: [number, number, number] | null = null;
  for (let i = 0; i <= segs; i++) {
    const t = a0 + span * (i / segs);
    const px = c.x + Math.cos(t) * ma.x + Math.sin(t) * mnx;
    const py = c.y + Math.cos(t) * ma.y + Math.sin(t) * mny;
    const [x, h, z] = mapCoord(px, py, elevation);
    if (prev) edges.push({ x1: prev[0], h1: prev[1], z1: prev[2], x2: x, h2: h, z2: z, color });
    prev = [x, h, z];
  }
  return edges;
}

/** Extract edges from a heavyweight POLYLINE entity (2D/3D) */
function extractPolylineEdges(entity: IEntity, layers: Record<string, ILayer> | undefined): EdgeSegment[] {
  const e = entity as any;
  const vertices = e.vertices as IPoint[] | undefined;
  if (!vertices || vertices.length < 2) return [];
  const color = resolveEntityColor(entity, layers);
  const closed: boolean = !!(e.shape || (typeof e.flags === "number" && (e.flags & 1)));
  const edges: EdgeSegment[] = [];
  for (let i = 0; i < vertices.length - 1; i++) {
    const [x1, h1, z1] = mapCoord(vertices[i].x, vertices[i].y, vertices[i].z ?? 0);
    const [x2, h2, z2] = mapCoord(vertices[i + 1].x, vertices[i + 1].y, vertices[i + 1].z ?? 0);
    edges.push({ x1, h1, z1, x2, h2, z2, color });
  }
  if (closed && vertices.length > 2) {
    const a = vertices[vertices.length - 1], b = vertices[0];
    const [x1, h1, z1] = mapCoord(a.x, a.y, a.z ?? 0);
    const [x2, h2, z2] = mapCoord(b.x, b.y, b.z ?? 0);
    edges.push({ x1, h1, z1, x2, h2, z2, color });
  }
  return edges;
}

/**
 * Parse a DXF file and return only edge segments (no geometry).
 * Used to draw the DXF as a line overlay onto the point cloud.
 */
export async function parseDxfEdges(
  url: string,
  onProgress?: (msg: string) => void
): Promise<EdgeSegment[]> {
  onProgress?.("Fetching DXF file...");
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`DXF fetch failed: HTTP ${response.status}`);
  }
  const text = await response.text();

  onProgress?.("Parsing DXF...");
  const parser = new DxfParser();
  const dxf: IDxf | null = parser.parse(text);
  if (!dxf) {
    throw new Error("Failed to parse DXF file");
  }

  const entities = dxf.entities ?? [];
  const layers = dxf.tables?.layer?.layers;

  const allEdges: EdgeSegment[] = [];
  let processed = 0;
  let skipped = 0;

  for (const entity of entities) {
    let edges: EdgeSegment[] = [];

    switch (entity.type) {
      case "LINE":
        edges = extractLineEdges(entity, layers);
        break;
      case "LWPOLYLINE":
        edges = extractLwPolylineEdges(entity, layers);
        break;
      case "SPLINE":
        edges = extractSplineEdges(entity, layers);
        break;
      case "ARC":
        edges = extractArcEdges(entity, layers);
        break;
      case "CIRCLE":
        edges = extractCircleEdges(entity, layers);
        break;
      case "ELLIPSE":
        edges = extractEllipseEdges(entity, layers);
        break;
      case "POLYLINE":
        edges = extractPolylineEdges(entity, layers);
        break;
      default:
        skipped++;
        continue;
    }

    if (edges.length > 0) {
      for (const e of edges) allEdges.push(e);
      processed++;
    } else {
      skipped++;
    }
  }

  onProgress?.(`DXF: ${allEdges.length} edges from ${processed} entities (${skipped} skipped)`);
  return allEdges;
}
